/*----------------------------------
- SECTION: SOURCE ANGLE GRID
----------------------------------*/

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { getAppraisalProvider } from '@uniquedomains/core/domains/appraisal';

import {
    CARD_TITLE_CLASS,
    SNAP_ITEM_CLASS,
    SNAP_ROW_CLASS,
    WHITE_CARD_CLASS,
} from '@/client/pages/landing/components/shared/pageStyles';
import type {
    PRODUCT_APPRAISE_PAGE_CONTENT,
    ProductAppraiseSource,
} from '@/client/catalogs/landing/product/appraise/pageContent';

/*----------------------------------
- TYPES
----------------------------------*/

type SourceAngleGridProps = {
    items: readonly ProductAppraiseSource[];
    labels: (typeof PRODUCT_APPRAISE_PAGE_CONTENT)['angles'];
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

/** Live and soon read as two states of one badge, so only the inks differ. */
const LIVE_BADGE_CLASS = 'bg-[#eaf6ef] text-[#2d7a55]';
const SOON_BADGE_CLASS = 'bg-[#edeae2] text-[#66696b]';
const NOTE_ROW_CLASS = 'm-0 flex gap-2.5 text-[12.5px] leading-[1.5] text-[#66696b] lg:text-[13.5px]';

/*----------------------------------
- COMPONENT
----------------------------------*/

/**
 * One card per appraisal source: what it is looking at, what it rewards, and what
 * it cannot see. Identity and live/soon state come from the provider catalog, so a
 * newly wired source drops its Soon badge here without a copy edit.
 *
 * Four columns on desktop, a swipe row on a phone, from one tree.
 */
const SourceAngleGrid = ({ items, labels }: SourceAngleGridProps) => (
    <>
        {/* The tinted panel is desktop-only: on a phone the cards bleed past the
            screen edge as a snap row, which a padded panel would cut off. */}
        <div class="mt-[18px] lg:mt-9 lg:rounded-[32px] lg:bg-[#f1ece6] lg:p-3">
            <div class={`${SNAP_ROW_CLASS} lg:grid lg:grid-cols-4 lg:gap-3`}>
                {items.map((item) => {
                    const provider = getAppraisalProvider(item.id);
                    if (!provider) return null;

                    const isLive = provider.status === 'live';

                    return (
                        <article
                            key={item.id}
                            data-reveal
                            class={`${SNAP_ITEM_CLASS} ${WHITE_CARD_CLASS} flex flex-col gap-3 p-[18px] lg:gap-3.5 lg:p-6`}
                        >
                            <div class="flex items-center justify-between gap-2">
                                <h3 class={CARD_TITLE_CLASS}>{provider.label}</h3>
                                <span
                                    class={`flex-none rounded-full px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-[0.08em] ${isLive ? LIVE_BADGE_CLASS : SOON_BADGE_CLASS}`}
                                >
                                    {isLive ? 'Live' : 'Soon'}
                                </span>
                            </div>

                            {/* The reserved height keeps the four dividers on one line
                                across the desktop row, where the cards sit side by side. */}
                            <p class="m-0 text-sm leading-[1.5] lg:min-h-[70px] lg:text-[15px] lg:leading-[1.55]">
                                {item.angle}
                            </p>

                            <div class="flex flex-col gap-2 border-t border-[#ece8df] pt-3 lg:gap-2.5 lg:pt-3.5">
                                <p class={NOTE_ROW_CLASS}>
                                    <span class="flex-none font-extrabold text-[#2d7a55] lg:w-[52px]">
                                        {labels.weighsLabel}
                                    </span>
                                    <span>{item.weighs}</span>
                                </p>
                                <p class={NOTE_ROW_CLASS}>
                                    <span class="flex-none font-extrabold text-[#b1475a] lg:w-[52px]">
                                        {labels.missesLabel}
                                    </span>
                                    <span>{item.misses}</span>
                                </p>
                            </div>
                        </article>
                    );
                })}
            </div>
        </div>

        <p class="mt-2.5 text-[12.5px] font-semibold text-[#66696b] lg:hidden">
            {items.length} {labels.swipeHint}
        </p>
    </>
);

export default SourceAngleGrid;
